'use client'

import { useCallback, useEffect, useState } from 'react'

// The fulfillment wizard, in-flow on the client edit view: provisioning
// step statuses with run/retry, one-time MCP token display, starter-KB
// seeding, owner invite, and demo→client conversion. Everything drives the
// /api/fulfillment routes with the admin session cookie; the panel never
// sees provider secrets (the status route strips them server-side).

const STEP_LABELS = {
  managed_account: 'Carrier sub-account',
  phone_number: 'Phone number',
  mcp_token: 'Data-tools token',
  assistant: 'AI receptionist',
  wiring: 'Number → call flow',
}

const STEP_DOT = {
  done: 'var(--theme-success-500, #2e7d32)',
  skipped: 'var(--theme-success-500, #2e7d32)',
  running: 'var(--theme-warning-500, #b26a00)',
  failed: 'var(--theme-error-500, #b00020)',
  pending: 'var(--theme-elevation-300)',
}

const card = {
  border: '1px solid var(--theme-elevation-150)',
  borderRadius: 8,
  padding: '14px 16px',
  background: 'var(--theme-elevation-50)',
}

const buttonStyle = (disabled) => ({
  padding: '6px 14px',
  borderRadius: 6,
  border: '1px solid var(--theme-elevation-250)',
  background: disabled ? 'var(--theme-elevation-100)' : 'var(--theme-elevation-0)',
  color: 'inherit',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 13,
})

export function ReceptionistPanelClient({ clientId }) {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [mcpToken, setMcpToken] = useState(null)
  const [convertName, setConvertName] = useState('')
  const [convertEmail, setConvertEmail] = useState('')
  const [convertedUrl, setConvertedUrl] = useState('')

  const load = useCallback(async () => {
    const res = await fetch(`/api/fulfillment/status?clientId=${encodeURIComponent(clientId)}`, {
      credentials: 'include',
    })
    if (res.ok) setData(await res.json())
  }, [clientId])

  useEffect(() => {
    load()
  }, [load])

  const act = async (label, url, body) => {
    setBusy(label)
    setError('')
    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(json.error || `${label} failed`)
        return null
      }
      await load()
      return json
    } finally {
      setBusy('')
    }
  }

  if (!data) return null
  const { client, run, kbCount, ownerInvited } = data
  const steps = run?.steps || []
  const provisioned = client.telephonyConnected
  const anyFailed = steps.some((s) => s.stepStatus === 'failed')

  return (
    <div style={{ ...card, marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>
          Receptionist fulfillment{client.isDemo ? ' · DEMO TENANT' : ''}
        </h3>
        {client.phoneNumber && (
          <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
            {client.phoneNumber} {provisioned ? '· connected' : '· not wired'}
          </span>
        )}
      </div>

      {error && (
        <p style={{ color: 'var(--theme-error-500, #b00020)', fontSize: 13, margin: '10px 0 0' }}>
          {error}
        </p>
      )}

      {/* 1 — Provisioning */}
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <strong style={{ fontSize: 13 }}>1 · Provision telephony</strong>
          <button
            type="button"
            disabled={Boolean(busy) || (provisioned && !anyFailed)}
            style={buttonStyle(Boolean(busy) || (provisioned && !anyFailed))}
            onClick={async () => {
              const result = await act('provision', '/api/fulfillment/provision', {
                clientId,
              })
              if (result?.mcpToken) setMcpToken(result.mcpToken)
            }}
          >
            {busy === 'provision'
              ? 'Provisioning…'
              : provisioned && !anyFailed
                ? 'Provisioned'
                : anyFailed || run
                  ? 'Retry provisioning'
                  : 'Provision'}
          </button>
          {run && <span style={{ fontSize: 12, color: 'var(--theme-elevation-500)' }}>run: {run.status}</span>}
          {client.assistantId && (
            <button
              type="button"
              disabled={Boolean(busy)}
              style={buttonStyle(Boolean(busy))}
              onClick={() => act('resync', '/api/fulfillment/resync-assistant', { clientId })}
              title="Recompile this client's live assistant from the current niche template + business details (retention plays, prompt updates, hours changes)."
            >
              {busy === 'resync' ? 'Re-syncing…' : 'Re-sync assistant'}
            </button>
          )}
        </div>
        {steps.length > 0 && (
          <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'grid', gap: 6 }}>
            {steps.map((s) => (
              <li key={s.key} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 13 }}>
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    background: STEP_DOT[s.stepStatus] || STEP_DOT.pending,
                    flexShrink: 0,
                  }}
                />
                <span style={{ minWidth: 150 }}>{STEP_LABELS[s.key] || s.key}</span>
                <span style={{ color: 'var(--theme-elevation-500)' }}>
                  {s.stepStatus}
                  {s.detail && s.stepStatus === 'failed' ? ` — ${String(s.detail).slice(0, 140)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
        {mcpToken && (
          <div
            style={{
              marginTop: 10,
              padding: '10px 12px',
              borderRadius: 6,
              border: '1px solid var(--theme-warning-500, #b26a00)',
              fontSize: 12,
            }}
          >
            <strong>Data-tools token — shown once, copy it now:</strong>
            <div style={{ fontFamily: 'monospace', wordBreak: 'break-all', marginTop: 6 }}>{mcpToken}</div>
          </div>
        )}
      </div>

      {/* 2 — Knowledge base */}
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
        <strong style={{ fontSize: 13 }}>2 · Knowledge base</strong>
        {kbCount > 0 ? (
          <span style={{ fontSize: 13, color: 'var(--theme-elevation-500)' }}>{kbCount} articles</span>
        ) : (
          <button
            type="button"
            disabled={Boolean(busy)}
            style={buttonStyle(Boolean(busy))}
            onClick={() => act('seed-kb', '/api/fulfillment/seed-kb', { clientId })}
          >
            {busy === 'seed-kb' ? 'Seeding…' : 'Seed starter KB'}
          </button>
        )}
      </div>

      {/* 3 — Owner invite */}
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
        <strong style={{ fontSize: 13 }}>3 · Owner invite</strong>
        {ownerInvited ? (
          <span style={{ fontSize: 13, color: 'var(--theme-success-500, #2e7d32)' }}>
            Invited ({client.ownerEmail})
          </span>
        ) : (
          <>
            <button
              type="button"
              disabled={Boolean(busy) || !client.ownerEmail}
              style={buttonStyle(Boolean(busy) || !client.ownerEmail)}
              onClick={() => act('invite', '/api/fulfillment/invite', { clientId })}
            >
              {busy === 'invite' ? 'Inviting…' : `Invite ${client.ownerEmail || '(set owner email first)'}`}
            </button>
          </>
        )}
      </div>

      {/* Demo conversion */}
      {client.isDemo && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--theme-elevation-150)' }}>
          <strong style={{ fontSize: 13 }}>Convert this demo into a real client</strong>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <input
              placeholder="Business name"
              value={convertName}
              onChange={(e) => setConvertName(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--theme-elevation-250)', fontSize: 13 }}
            />
            <input
              placeholder="Owner email (optional)"
              value={convertEmail}
              onChange={(e) => setConvertEmail(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--theme-elevation-250)', fontSize: 13 }}
            />
            <button
              type="button"
              disabled={Boolean(busy) || !convertName.trim()}
              style={buttonStyle(Boolean(busy) || !convertName.trim())}
              onClick={async () => {
                const result = await act('convert', '/api/fulfillment/convert-demo', {
                  demoClientId: clientId,
                  name: convertName.trim(),
                  ownerEmail: convertEmail.trim(),
                })
                if (result?.adminUrl) setConvertedUrl(result.adminUrl)
              }}
            >
              {busy === 'convert' ? 'Creating…' : 'Create client'}
            </button>
          </div>
          {convertedUrl && (
            <p style={{ fontSize: 13, marginTop: 8 }}>
              Created —{' '}
              <a href={convertedUrl} style={{ textDecoration: 'underline' }}>
                open the new client
              </a>{' '}
              to finish details, provision, and invite.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
